import { Button, Popconfirm, Space } from "antd";
import uniq from "lodash/uniq";
import { FC, useState } from "react";
import {
  BsFillHandThumbsUpFill,
  BsHandThumbsUp,
  BsTrash,
} from "react-icons/bs";

import CardUserProfile from "@/components/Card/card-user-profile";
import useAdminName from "@/hooks/useAdminName";
import { useIsViewAsHR } from "@/hooks/useIsViewAsHR";
import { useDeleteComment, useUpdateComment } from "@/hooks/usePost";
import useAuthStore from "@/store/useAuthStore";
import { IDataDeletePost, IUpdateComment } from "@/types";
import { formatNumberKM } from "@/utils/helper";

const ProfileCardComment: FC<{
  dataComment: any;
  onSuccess?: any;
  onError?: any;
  handleOpenModalRedFlag?: () => void;
}> = ({ dataComment, onSuccess, onError, handleOpenModalRedFlag }) => {
  const authUser = useAuthStore.useUser();

  const { isViewAsHR } = useIsViewAsHR();

  const [isProcessDeleteComment, setIsProcessDeleteComment] =
    useState<boolean>(false);
  const [isProcessLike, setIsProcessLike] = useState<boolean>(false);
  const [likeCount, setLikeCount] = useState<number>(
    uniq(dataComment.likeUsers).length || 0
  );
  const [isLiked, setIsLiked] = useState<boolean>(
    dataComment?.likeUsers?.includes(authUser?.profile?.user_id) || false
  );

  const handleLikeComment = async () => {
    setIsLiked((liked) => {
      setIsProcessLike(true);
      setLikeCount((count) => count + (isLiked ? -1 : 1));
      postLikeComment(!liked, dataComment);
      return !liked;
    });
  };

  const postLikeComment = async (isLiked: boolean, dataComment: any) => {
    const dataLikeUsersCurrent: string[] = uniq(dataComment?.likeUsers) || [];
    let dataLikeUsers: string[] = [];

    setIsProcessLike(true);

    try {
      dataLikeUsers = isLiked
        ? dataLikeUsersCurrent?.filter(
            (id: string) => id !== authUser?.profile?.user_id
          )
        : [...dataLikeUsersCurrent, authUser?.profile?.user_id];

      const payload: IUpdateComment = {
        id: dataComment?.id,
        _version: dataComment?._version,
        likeUsers: dataLikeUsers,
      };

      await useUpdateComment(payload);

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      setLikeCount((count) => count - (isLiked ? 1 : 0));
      setIsLiked((liked) => !liked);
      if (onError) {
        onError();
      }
    } finally {
      setIsProcessLike(false);
    }
  };
  const handleDeleteComment = async () => {
    setIsProcessDeleteComment(true);

    try {
      const payload: IDataDeletePost = {
        id: dataComment?.id,
        _version: dataComment?._version,
      };

      await useDeleteComment(payload);

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      if (onError) {
        onError();
      }
    } finally {
      setIsProcessDeleteComment(false);
    }
  };

  const adminName = useAdminName();

  return (
    <div className="flex gap-4 items-center">
      <div>
        <CardUserProfile
          findCG={dataComment?.author?.id !== authUser?.bio?.user?.id}
          item={
            dataComment?.isAuthorHR
              ? {
                  ...dataComment?.author,
                  firstName: adminName,
                  lastName: "",
                  avatarUrl: "",
                }
              : dataComment?.author
          }
          points={dataComment?.author?.points}
          cgPercent={
            dataComment?.author?.id === authUser?.bio?.user?.id
              ? 100
              : undefined
          }
          avatarSize="sm"
          variant="horizontal"
          avatarOnly
          reference="comment"
          hideJobType={dataComment?.isAuthorHR}
          hideType={dataComment?.isAuthorHR}
        />
      </div>
      <div>
        <p className="text-xs font-medium text-neutral-300 mb-1">
          {dataComment?.isAuthorHR
            ? adminName
            : `${dataComment?.author?.firstName} ${dataComment?.author?.lastName}`}
        </p>
        <p className="text-base text-white mb-1">{dataComment?.message}</p>
        <Space>
          <Button
            icon={
              isLiked ? (
                <BsFillHandThumbsUpFill key="like" />
              ) : (
                <BsHandThumbsUp key="like" />
              )
            }
            type="text"
            loading={isProcessLike}
            onClick={() => handleLikeComment()}
            size="small"
            disabled={isViewAsHR}
          >
            {likeCount > 0 &&
              formatNumberKM(likeCount)}{" "}
            Like
            {likeCount > 1 && "s"}
          </Button>

          {dataComment?.commentAuthorId !== authUser?.profile?.user_id ||
          isViewAsHR ? (
            <Button
              type="text"
              size="small"
              onClick={() => handleOpenModalRedFlag!()}
            >
              Report
            </Button>
          ) : (
            <Popconfirm
              title="Delete Cooment"
              description="Are you sure you want to delete this comment?"
              okText="Yes"
              cancelText="No"
              onConfirm={() => handleDeleteComment()}
            >
              <Button
                icon={<BsTrash key="delete" />}
                type="text"
                loading={isProcessDeleteComment}
              >
                Delete
              </Button>
            </Popconfirm>
          )}
        </Space>
      </div>
    </div>
  );
};

export default ProfileCardComment;
